import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import {
  Perm,
  type Adjustment,
  type AdjustmentDetail,
  type AdjustmentReason,
  type Product,
  type ProductListItem,
  type StockOnHandRow,
  type Warehouse,
} from '../api/types'
import { formatMoney, formatMoment, formatQty, toNumber } from '../ui/format'
import {
  Banner, Button, Empty, ErrorBanner, Field, NumberInput, Panel, Row, Select, Spinner, TextInput,
} from '@shop/ui'
import { adjustmentStatusNames } from '../ui/purchasing'

/**
 * تسوياتُ المخزون.
 *
 * <b>ولا تتحوّل هذه الشاشةُ إلى تعديلٍ مباشرٍ للرصيد</b>: الرصيدُ الحاليُّ يُعرض
 * <b>للقراءة وحدَها</b>، والموظّفُ يُدخل <b>الفرق</b> واتّجاهَه — لا الرقمَ
 * النهائيّ. ولو أدخل النهائيَّ لَصار النظامُ يحسب الفرقَ عنه، وضاع أنّ ما وقع
 * حركةٌ لا كتابةُ رقم.
 */
export default function Adjustments() {
  const { can } = useAuth()
  const [status, setStatus] = useState('')

  const adjustments = useQuery({
    queryKey: ['adjustments', status],
    queryFn: () => api<Adjustment[]>(`/api/v1/adjustments?status=${status}`),
  })

  return (
    <Panel
      title="تسوياتُ المخزون"
      actions={can(Perm.adjustmentPost) && <NewAdjustmentButton />}
    >
      <div className="mb-4">
        <Field label="الحالة">
          <Select value={status} onChange={setStatus}>
            <option value="">الكلّ</option>
            <option value="DRAFT">مسوّدة</option>
            <option value="POSTED">مُرحَّلة</option>
            <option value="REVERSED">معكوسة</option>
          </Select>
        </Field>
      </div>

      {adjustments.isPending && <Spinner />}
      {adjustments.isError && <ErrorBanner error={adjustments.error} />}
      {adjustments.data?.length === 0 && <Empty>لا تسوياتٍ مطابقة.</Empty>}

      <ul className="space-y-2">
        {(adjustments.data ?? []).map((document) => (
          <li key={document.id}>
            <Link
              to={`/adjustments/${document.id}`}
              className="block rounded-md border border-line px-3 py-2 hover:bg-surface"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium">{document.reasonName}</span>
                <span className="tabular text-xs text-ink-soft" dir="ltr">{document.adjustmentNo}</span>
              </div>

              <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                <span>{adjustmentStatusNames[document.status] ?? document.status}</span>
                {document.increasedQty > 0 && (
                  <span className="tabular text-ok">+{formatQty(document.increasedQty)}</span>
                )}
                {document.decreasedQty > 0 && (
                  <span className="tabular text-warn">−{formatQty(document.decreasedQty)}</span>
                )}
                <span className="tabular">{document.adjustmentDate}</span>
              </div>

              <p className="mt-1 text-xs text-ink-soft">{document.note}</p>
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  )
}

function NewAdjustmentButton() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const [warehouseId, setWarehouseId] = useState('')
  const [reasonCode, setReasonCode] = useState('')
  const [note, setNote] = useState('')

  const warehouses = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => api<Warehouse[]>('/api/v1/warehouses'),
    enabled: open,
  })

  // الأسبابُ **من الجدول** لا من قائمةٍ في الواجهة: رمزٌ يُشار إليه من حركاتٍ
  // لا تُعدَّل، فقائمةٌ في الكود تتفرّق عنه أوّلَ تعديل.
  const reasons = useQuery({
    queryKey: ['adjustmentReasons'],
    queryFn: () => api<AdjustmentReason[]>('/api/v1/adjustments/reasons'),
    enabled: open,
  })

  const create = useMutation({
    mutationFn: () =>
      api<Adjustment>('/api/v1/adjustments', {
        method: 'POST',
        body: { warehouseId, reasonCode, note },
      }),
    onSuccess: async (document) => {
      await queryClient.invalidateQueries({ queryKey: ['adjustments'] })
      setOpen(false)
      void navigate(`/adjustments/${document.id}`)
    },
  })

  if (!open) {
    return <Button tone="primary" onClick={() => setOpen(true)}>تسويةٌ جديدة</Button>
  }

  const chosen = reasons.data?.find((r) => r.code === reasonCode)

  return (
    <div className="w-full space-y-3 rounded-md border border-line bg-surface p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="المستودع">
          <Select value={warehouseId} onChange={setWarehouseId}>
            <option value="">اختر مستودعًا…</option>
            {(warehouses.data ?? []).map((w) => (
              <option key={w.id} value={w.id}>{w.nameAr}</option>
            ))}
          </Select>
        </Field>

        <Field
          label="السبب"
          hint={
            chosen?.direction === 1 ? 'زيادةٌ فقط'
              : chosen?.direction === -1 ? 'نقصٌ فقط'
                : chosen ? 'يقبل الزيادةَ والنقص' : undefined
          }
        >
          <Select value={reasonCode} onChange={setReasonCode}>
            <option value="">اختر سببًا…</option>
            {(reasons.data ?? []).map((r) => (
              <option key={r.code} value={r.code}>{r.nameAr}</option>
            ))}
          </Select>
        </Field>
      </div>

      <Field label="الملاحظة" hint="عشرةُ أحرفٍ فأكثر — «تلف» لا يقول أيَّ صندوقٍ ولا لماذا">
        <TextInput value={note} onChange={setNote} placeholder="سقط الصندوقُ عن الرفّ وانكسر" />
      </Field>

      {create.isError && <ErrorBanner error={create.error} />}

      <div className="flex gap-2">
        <Button
          tone="primary"
          disabled={!warehouseId || !reasonCode || note.trim().length < 10 || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'تُنشأ…' : 'إنشاء'}
        </Button>

        <Button onClick={() => setOpen(false)}>إلغاء</Button>
      </div>
    </div>
  )
}

export function AdjustmentDetailScreen() {
  const { id } = useParams<{ id: string }>()
  const { can } = useAuth()

  const document = useQuery({
    queryKey: ['adjustment', id],
    queryFn: () => api<AdjustmentDetail>(`/api/v1/adjustments/${id}`),
    enabled: Boolean(id),
  })

  const reasons = useQuery({
    queryKey: ['adjustmentReasons'],
    queryFn: () => api<AdjustmentReason[]>('/api/v1/adjustments/reasons'),
  })

  if (document.isPending) {
    return <Spinner />
  }

  if (document.isError) {
    return <ErrorBanner error={document.error} />
  }

  const { header, lines } = document.data!
  const isDraft = header.status === 'DRAFT'
  const reason = reasons.data?.find((r) => r.code === header.reasonCode)

  return (
    <div className="space-y-4">
      <Panel title={`تسوية ${header.adjustmentNo}`}>
        <dl className="space-y-1 text-sm">
          <Row label="السبب">{header.reasonName}</Row>
          <Row label="الحالة">{adjustmentStatusNames[header.status] ?? header.status}</Row>
          <Row label="الملاحظة">{header.note}</Row>
          <Row label="التاريخ">{header.adjustmentDate}</Row>
          {header.postedAt && <Row label="رُحِّلت">{formatMoment(header.postedAt)}</Row>}
          {header.reverseReason && <Row label="سببُ العكس">{header.reverseReason}</Row>}
        </dl>

        <div className="mt-4 flex flex-wrap gap-2">
          {isDraft && lines.length > 0 && can(Perm.adjustmentPost) && (
            <PostControl adjustmentId={header.id} />
          )}

          {header.status === 'POSTED' && can(Perm.movementReverse) && (
            <ReverseControl adjustmentId={header.id} />
          )}
        </div>
      </Panel>

      <Panel title="السطور">
        {lines.length === 0 && <Empty>لا سطورَ بعد.</Empty>}

        <ul className="space-y-2">
          {lines.map((line) => (
            <li key={line.id} className="rounded-md border border-line px-3 py-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium">{line.productName}</span>
                <span className="tabular text-xs text-ink-soft" dir="ltr">{line.sku}</span>
              </div>

              <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                <span className={`tabular ${line.direction === 1 ? 'text-ok' : 'text-warn'}`}>
                  {line.direction === 1 ? '+' : '−'}{formatQty(line.qty)}
                </span>

                {can(Perm.costView) && line.unitCost !== null && (
                  <span className="tabular">بكلفة {formatMoney(line.unitCost)}</span>
                )}
              </div>
            </li>
          ))}
        </ul>

        {isDraft && can(Perm.adjustmentPost) && (
          <div className="mt-3">
            <AdjustmentLineEditor
              document={header}
              lines={lines}
              fixedDirection={reason?.direction ?? null}
            />
          </div>
        )}
      </Panel>
    </div>
  )
}

type DraftLine = { variantId: string; label: string; qty: string; direction: number }

/**
 * تحريرُ السطور — <b>الفرقُ واتّجاهُه، لا الرقمُ النهائيّ</b>.
 *
 * والرصيدُ الحاليُّ يُعرض للقراءة وحدَها: يعرف الموظّفُ ماذا يوجد، ويكتب كم
 * زاد أو نقص. ولو كتب النهائيَّ لَصارت الشاشةُ تعديلًا مباشرًا للرصيد بواجهةٍ
 * أخرى.
 */
function AdjustmentLineEditor({
  document,
  lines,
  fixedDirection,
}: {
  document: Adjustment
  lines: AdjustmentDetail['lines']
  fixedDirection: number | null
}) {
  const queryClient = useQueryClient()

  const [draft, setDraft] = useState<DraftLine[]>(() =>
    lines.map((l) => ({
      variantId: l.variantId,
      label: `${l.productName} · ${l.sku}`,
      qty: l.qty.toString(),
      direction: l.direction,
    })),
  )

  const save = useMutation({
    mutationFn: () =>
      api<AdjustmentDetail>(`/api/v1/adjustments/${document.id}/lines`, {
        method: 'PUT',
        body: {
          lines: draft.map((d) => ({
            variantId: d.variantId,
            qty: toNumber(d.qty) ?? 0,
            direction: d.direction,
          })),
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['adjustment', document.id] })
    },
  })

  const incomplete = draft.some((d) => (toNumber(d.qty) ?? 0) <= 0)

  return (
    <div className="space-y-3">
      {draft.map((line, index) => (
        <div key={`${line.variantId}-${index}`} className="rounded-md border border-line p-3">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <span className="text-sm font-medium">{line.label}</span>
            <Button onClick={() => setDraft(draft.filter((_, i) => i !== index))}>حذف</Button>
          </div>

          <CurrentStock variantId={line.variantId} warehouseId={document.warehouseId} />

          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <Field label="الفرق" hint="كم زاد أو نقص — لا الرصيدُ النهائيّ">
              <NumberInput
                value={line.qty}
                onChange={(v) => setDraft(draft.map((d, i) => (i === index ? { ...d, qty: v } : d)))}
              />
            </Field>

            <Field label="الاتّجاه">
              <Select
                value={line.direction.toString()}
                onChange={(v) =>
                  setDraft(draft.map((d, i) => (i === index ? { ...d, direction: Number(v) } : d)))
                }
                disabled={fixedDirection !== null}
              >
                <option value="1">زيادة</option>
                <option value="-1">نقص</option>
              </Select>
            </Field>
          </div>
        </div>
      ))}

      <VariantPicker
        onPick={(variantId, label) =>
          setDraft([...draft, { variantId, label, qty: '1', direction: fixedDirection ?? -1 }])
        }
      />

      {save.isError && <ErrorBanner error={save.error} />}

      <Button
        tone="primary"
        disabled={draft.length === 0 || incomplete || save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? 'تُحفظ…' : 'حفظُ السطور'}
      </Button>
    </div>
  )
}

/** الرصيدُ الحاليُّ — <b>للعرض وحدَه</b>، فالموظّفُ يعرف ماذا يوجد قبل أن يكتب الفرق. */
function CurrentStock({ variantId, warehouseId }: { variantId: string; warehouseId: string }) {
  const stock = useQuery({
    queryKey: ['stock', variantId, warehouseId, true],
    queryFn: () =>
      api<StockOnHandRow[]>(
        `/api/v1/stock/on-hand?variantId=${variantId}&warehouseId=${warehouseId}&includeZero=true`),
  })

  const qty = stock.data?.[0]?.qtyOnHand ?? 0

  return (
    <p className="text-xs text-ink-soft">
      الرصيدُ الآن في هذا المستودع: <span className="tabular">{stock.isPending ? '…' : formatQty(qty)}</span>
    </p>
  )
}

function VariantPicker({ onPick }: { onPick: (variantId: string, label: string) => void }) {
  const [term, setTerm] = useState('')
  const [productId, setProductId] = useState<string | null>(null)

  const results = useQuery({
    queryKey: ['products', term],
    queryFn: () => api<ProductListItem[]>(`/api/v1/products?limit=20&q=${encodeURIComponent(term)}`),
    enabled: term.trim().length > 1 && productId === null,
  })

  const product = useQuery({
    queryKey: ['product', productId],
    queryFn: () => api<Product>(`/api/v1/products/${productId}`),
    enabled: productId !== null,
  })

  if (productId !== null) {
    return (
      <div className="rounded-md border border-dashed border-line p-3">
        {product.isPending && <Spinner />}

        {product.data && (
          <>
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium">{product.data.nameAr}</span>
              <Button onClick={() => setProductId(null)}>تراجع</Button>
            </div>

            <ul className="space-y-1">
              {product.data.variants.filter((v) => v.isActive).map((variant) => (
                <li key={variant.id}>
                  <button
                    type="button"
                    className="w-full rounded-md px-2 py-1 text-start text-sm hover:bg-surface"
                    onClick={() => {
                      const parts = [variant.variantName, variant.colour, variant.capacity].filter(Boolean)
                      onPick(variant.id, `${product.data!.nameAr} · ${parts.join(' · ') || 'الافتراضيّ'}`)
                      setProductId(null)
                      setTerm('')
                    }}
                  >
                    {[variant.variantName, variant.colour, variant.capacity].filter(Boolean).join(' · ')
                      || 'المتغيّرُ الافتراضيّ'}
                    <span className="tabular mx-2 text-xs text-ink-soft" dir="ltr">{variant.sku}</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="rounded-md border border-dashed border-line p-3">
      <Field label="أضِف صنفًا">
        <TextInput value={term} onChange={setTerm} placeholder="ابحث بالاسم أو الطراز…" />
      </Field>

      {results.isPending && term.trim().length > 1 && <Spinner />}

      <ul className="mt-2 space-y-1">
        {(results.data ?? []).map((row) => (
          <li key={row.id}>
            <button
              type="button"
              className="w-full rounded-md px-2 py-1 text-start text-sm hover:bg-surface"
              onClick={() => setProductId(row.id)}
            >
              {row.nameAr}
              <span className="tabular mx-2 text-xs text-ink-soft" dir="ltr">{row.code}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

function PostControl({ adjustmentId }: { adjustmentId: string }) {
  const queryClient = useQueryClient()

  const post = useMutation({
    mutationFn: () =>
      api<AdjustmentDetail>(`/api/v1/adjustments/${adjustmentId}/post`, { method: 'POST', body: {} }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['adjustment', adjustmentId] })
      await queryClient.invalidateQueries({ queryKey: ['adjustments'] })
      await queryClient.invalidateQueries({ queryKey: ['stock'] })
    },
  })

  return (
    <div className="w-full space-y-2">
      <Banner tone="warn">
        الترحيلُ يكتب حركةً في الدفتر بسببها ونصِّها. <b>ولا تُعدَّل بعده</b> —
        تُصحَّح بعكسٍ لا بتعديل.
      </Banner>

      {post.isError && <ErrorBanner error={post.error} />}

      <Button tone="primary" disabled={post.isPending} onClick={() => post.mutate()}>
        {post.isPending ? 'تُرحَّل…' : 'ترحيلُ التسوية'}
      </Button>
    </div>
  )
}

function ReverseControl({ adjustmentId }: { adjustmentId: string }) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [asking, setAsking] = useState(false)

  const reverse = useMutation({
    mutationFn: () =>
      api<AdjustmentDetail>(`/api/v1/adjustments/${adjustmentId}/reverse`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['adjustment', adjustmentId] })
      await queryClient.invalidateQueries({ queryKey: ['stock'] })
      setAsking(false)
    },
  })

  if (!asking) {
    return <Button onClick={() => setAsking(true)}>عكسُ التسوية</Button>
  }

  return (
    <div className="w-full space-y-2">
      <Banner tone="warn">
        العكسُ <b>حركةٌ جديدةٌ تشير إلى الأصل</b> — ولا تُمحى التسويةُ ولا تُعدَّل.
      </Banner>

      <Field label="سببُ العكس" hint="عشرةُ أحرفٍ فأكثر">
        <TextInput value={reason} onChange={setReason} placeholder="ظهرت البضاعةُ في مستودعٍ آخر" />
      </Field>

      {reverse.isError && <ErrorBanner error={reverse.error} />}

      <div className="flex gap-2">
        <Button
          tone="danger"
          disabled={reason.trim().length < 10 || reverse.isPending}
          onClick={() => reverse.mutate()}
        >
          {reverse.isPending ? 'يُعكَس…' : 'تأكيدُ العكس'}
        </Button>

        <Button onClick={() => setAsking(false)}>تراجع</Button>
      </div>
    </div>
  )
}
